import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteOtherCover,
  editSingleSavingsAppliedPolicy,
  getSavingsPolicyConfig,
  getSavingsPolicyCover,
  getSingleAppliedSavingsPolicy,
} from "../../../../redux/slice/policy/savingsPolicy/savingsPolicySlice";

import PageTitle from "../../../../components/partials/PageTitle";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import moment from "moment";
import { savingsPolicyValidator } from "../../../../utils/validators/validators";
import { savingsPolicyApplication } from "../../../../utils/validators/schema";
import { removeLeadingZeros } from "../../../../utils/helpers/filter";
import { categories } from "../../../../utils/data/account_type";
import Select from "./../../../../components/form/select/Select";
import { LoaderTwo } from "../../../../components/loader/LoaderTwo";
import useFileUpload from "../../../../utils/hooks/useFileUpload";
import { FileUploader } from "react-drag-drop-files";
import AddOtherSavingsCover from "../../../../components/modals/policy/AddOtherSavingsCover";
import { BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";

const EditSavingsApplication = () => {
  const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
  const [endDate, setEndDate] = useState(null);
  const [editData, setEditData] = useState(null);
  const repaymentRef = useRef();
  const uploadingRef = useRef();

  const {
    customer_policy_details,
    isLoading,
    temp_policy_attach_data,
    savingsPolicyConfig,
    covers,
    otherCovers,
  } = useSelector((state) => state.savingsPolicy);

  const { user, user_permissions } = useSelector((state) => state?.auth?.user);
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const isAllowed = user_permissions?.some(
      (permission) => permission.name === "edit applied policy"
    );
    if (!isAllowed) {
      navigate("/admin/customer-savings-policy-view", {
        state: {
          customerId: customer_policy_details?.policy_app_details?.customerId,
          policyAppNumber:
            customer_policy_details?.policy_app_details?.policyAppNumber,
        },
      });
    }
  }, [user_permissions, customer_policy_details, navigate]);

  const premiumPaymentRef = useRef(
    customer_policy_details?.policy_app_details?.premuim_contribution
  );

  const breadcrumb = {
    from: {
      title: "policy application view",
      link: "/admin/customer-savings-policy-view",
    },
    current: {
      title: "policy application edit",
      link: null,
    },
  };

  const {
    fileUrl,
    loading: uploadingProposalDoc,
    singleFileUpload,
  } = useFileUpload();

  const handleFileChange = (file) => {
    uploadingRef.current = true;
    singleFileUpload(file);
  };

  // const allCovers = useMemo(() => {
  //   const base = customer_policy_details?.policy_app_rsk ?? [];
  //   const extras = otherCovers ?? [];
  //   return [...base, ...extras];
  // }, [customer_policy_details?.policy_app_rsk, otherCovers]);

  const allCovers = useMemo(() => {
    const map = new Map();
    // 1) start with DB covers
    (customer_policy_details?.policy_app_rsk ?? []).forEach((c) => {
      if (c?.cover_id != null) map.set(c.cover_id, c);
    });
    // 2) override with local edits
    (otherCovers ?? []).forEach((c) => {
      if (c?.cover_id != null) map.set(c.cover_id, c);
    });
    return Array.from(map.values());
  }, [customer_policy_details?.policy_app_rsk, otherCovers]);

  const totalOtherPremium = useMemo(() => {
    return allCovers.reduce((acc, cov) => {
      const sumAssured = parseFloat(cov?.sum_assured ?? 0);
      const rate = parseFloat(cov?.rate ?? 0);
      const premium = (sumAssured * rate) / 100;
      return acc + (Number.isFinite(premium) ? premium : 0);
    }, 0);
  }, [allCovers]);

  const onSubmit = (values) => {
    if (uploadingProposalDoc) {
      return;
    }
    const { sum_assured, other_cover_total, ...rest } = values;
    const data = {
      ...rest,
      premium_contribution: premiumPaymentRef.current,
      policyId: customer_policy_details?.policy_app_details?.policyAppNumber,
      savings_policy_id: customer_policy_details?.policy_app_details?.policy_id,
      customer_id: customer_policy_details?.policy_app_details?.customerId,
      sum_assured: totalPremium,
      end_date: endDate,
      covers: allCovers,
    };
    if (fileUrl) {
      data.proposal_document = fileUrl;
    }
    console.log(data);
    dispatch(editSingleSavingsAppliedPolicy(data));
    getSingleAppliedSavingsPolicy({
      customer_id: customer_policy_details?.policy_app_details?.customerId,
      policy_number:
        customer_policy_details?.policy_app_details?.policyAppNumber,
    });
    navigate("/admin/customer-savings-policy-view", {
      state: {
        customerId: customer_policy_details?.policy_app_details?.customerId,
        policyAppNumber:
          customer_policy_details?.policy_app_details?.policyAppNumber,
      },
    });
  };
  // form control and validations
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      duration:
        customer_policy_details?.policy_app_details?.duration ??
        temp_policy_attach_data?.duration ??
        savingsPolicyConfig?.data?.p_gen_info?.[0]?.minimum_duration ??
        "",
      effective_date: moment(
        customer_policy_details?.policy_app_details?.effectiveDate
      ).format("YYYY-MM-DD"),
      repayment_frequency:
        customer_policy_details?.policy_app_details?.repayment_frequency || "",
      sum_assured:
        customer_policy_details?.policy_app_details?.sumAssured || "",
      interest_rate: customer_policy_details?.policy_app_details?.interest_rate,

      other_cover_total: null,
    },
    validationSchema: savingsPolicyApplication,
    validate: (values) => {
      const minimumDuration =
        savingsPolicyConfig?.data?.p_gen_info?.[0]?.minimum_duration || 0;
      const rates = savingsPolicyConfig?.data?.rate || [];
      return savingsPolicyValidator(values, minimumDuration, rates);
    },

    onSubmit,
  });

  useEffect(() => {
    setFieldValue("other_cover_total", totalOtherPremium, false);
  }, [totalOtherPremium, setFieldValue]);

  const totalPremium = useMemo(() => {
    const base = parseFloat(values?.sum_assured ?? 0);
    return (Number.isFinite(base) ? base : 0) + totalOtherPremium;
  }, [values?.sum_assured, totalOtherPremium]);

  useEffect(() => {
    if (values?.effective_date || values?.duration) {
      calculateEndDate();
    }
  }, [values?.effective_date, values?.duration]);

  useEffect(() => {
    if (state?.customerId) {
      const payload = {
        policy_id: state?.policy_id,
        office_id: user?.office_id,
      };
      resetForm();
      dispatch(
        getSingleAppliedSavingsPolicy({
          customer_id: state?.customerId,
          policy_number: state?.policyAppNumber,
        })
      );
      dispatch(getSavingsPolicyCover(payload));
      dispatch(getSavingsPolicyConfig(payload));
    }
  }, [
    dispatch,
    state?.customerId,
    state?.policyAppNumber,
    user?.office_id,
    state?.policy_id,
    resetForm,
  ]);

  // date manipulation starts here
  const calculateEndDate = () => {
    const effectiveDateObj = moment(values?.effective_date);
    const EndDateObj = effectiveDateObj
      .add(values?.duration, "months")
      .subtract(1, "days"); // Subtract 1 day
    const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");

    setEndDate(EndDateFormatted);
  };

  return (
    <>
      {/* page title */}
      <PageTitle title="Savings Policy Application" breadcrumb={breadcrumb} />
      <main className="card">
        <form className="card-body" onSubmit={handleSubmit}>
          <div className="border p-3 rounded-3 my-1 position-relative">
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Investment Information
            </h4>
            <div className="col-md-12">
              <div className="mb-3">
                <label htmlFor="policy_id" className="form-label">
                  Policy Name<span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  disabled
                  value={
                    customer_policy_details?.policy_app_details?.policyName
                  }
                  className="form-control"
                />
              </div>
              {savingsPolicyConfig && !isLoading ? (
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Duration (in months){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        id="duration"
                        name="duration"
                        className={`form-control form-control-sm no-arrows ${
                          errors?.duration && touched?.duration && "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values?.duration}
                        onInput={(e) => removeLeadingZeros(e.target.id)}
                        // placeholder='Enter Duration'
                      />
                      {errors?.duration && touched?.duration && (
                        <span className="text-danger">{errors?.duration}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Effective date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        id="effective_date"
                        name="effective_date"
                        className={`form-control form-control-sm no-arrows ${
                          errors?.effective_date &&
                          touched?.effective_date &&
                          "is-invalid"
                        }`}
                        value={values?.effective_date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.effective_date && touched?.effective_date && (
                        <span className="text-danger">
                          {errors?.effective_date}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        End date
                      </label>
                      <input
                        type="date"
                        id="end_date"
                        name="end_date"
                        value={endDate}
                        className={`form-control form-control-sm`}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">
                        Repayment Frequency{" "}
                        <span className="text-danger">*</span>
                      </label>

                      <div>
                        <Select
                          isError={
                            errors?.repayment_frequency &&
                            touched?.repayment_frequency
                          }
                          name="repayment_frequency"
                          ref={repaymentRef}
                          data={categories?.map((item) => ({
                            label: item?.name,
                            value: item?.name,
                          }))}
                          value={values.repayment_frequency}
                          onChange={(selectedOption) =>
                            setFieldValue(
                              "repayment_frequency",
                              selectedOption?.value
                            )
                          }
                          onBlur={() => {
                            handleBlur({
                              target: { name: "repayment_frequency" },
                            });
                          }}
                        />
                        {errors?.repayment_frequency &&
                          touched?.repayment_frequency && (
                            <p className="text-danger mb-0">
                              {errors?.repayment_frequency}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Sum Assured
                      </label>
                      <input
                        type="number"
                        id="sum_assured"
                        name="sum_assured"
                        value={values?.sum_assured}
                        className={`form-control form-control-sm no-arrows ${
                          errors?.sum_assured &&
                          touched?.sum_assured &&
                          "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.sum_assured && touched?.sum_assured && (
                        <span className="text-danger">
                          {errors?.sum_assured}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="policy_name" className="form-label">
                        Interest Rate
                      </label>
                      <input
                        type="number"
                        id="interest_rate"
                        name="interest_rate"
                        value={values?.interest_rate}
                        className={`form-control form-control-sm no-arrows ${
                          errors?.interest_rate &&
                          touched?.interest_rate &&
                          "is-invalid"
                        }`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors?.interest_rate && touched?.interest_rate && (
                        <span className="text-danger">
                          {errors?.interest_rate}
                        </span>
                      )}
                    </div>
                  </div>
                  {premiumPaymentRef?.current ? (
                    <div className="col-md-4">
                      <div className="mb-3">
                        <label htmlFor="policy_name" className="form-label">
                          Premium Contribution
                        </label>
                        <input
                          type="number"
                          value={
                            premiumPaymentRef.current ||
                            temp_policy_attach_data?.premuim_contribution
                          }
                          className={`form-control form-control-sm no-arrows`}
                          readOnly
                        />
                      </div>
                    </div>
                  ) : null}
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label
                        className="form-label d-block"
                        htmlFor="proposal_document"
                      >
                        <span
                          className={`${
                            (fileUrl ||
                              temp_policy_attach_data?.proposal_document) &&
                            !uploadingProposalDoc &&
                            "text-success"
                          }`}
                        >
                          Upload Proposal document
                        </span>
                        {!fileUrl && uploadingProposalDoc && (
                          <span>
                            <i className="mdi mdi-dots-circle mdi-spin "></i>
                          </span>
                        )}
                        {(fileUrl ||
                          temp_policy_attach_data?.proposal_document) &&
                          !uploadingProposalDoc && (
                            <span>
                              <i className="mdi mdi-check text-success fs-5"></i>
                            </span>
                          )}
                      </label>
                      <FileUploader
                        label="Browse Document"
                        children={
                          <div
                            className="borde border-dotted p-1 px-2 text-center cursor-p"
                            style={{ border: "2px dashed grey" }}
                          >
                            <i className="uil uil-cloud-upload fs-4"></i> Upload
                            Document
                            <span className="text-primary-dark mt-0"></span>
                          </div>
                        }
                        handleChange={handleFileChange}
                        onDrop={handleFileChange}
                        name="file"
                        types={fileTypes}
                      />
                      {temp_policy_attach_data?.proposal_document ? (
                        <a
                          href={
                            fileUrl ||
                            temp_policy_attach_data?.proposal_document
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-primary text-white align-items-center align-content-center justify-content-center ms-2"
                        >
                          <i className="mdi mdi-eye text-white fs-5"></i>
                        </a>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : isLoading ? (
                <LoaderTwo height={12} />
              ) : null}
            </div>
          </div>

          <div className="border p-3 rounded-3 my-3 position-relative">
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Risk Covered Information
            </h4>
            {savingsPolicyConfig && !isLoading ? (
              <p className="text-capitalize">
                <span className="fw-bold">Life Cover Name: </span>{" "}
                {customer_policy_details?.life_cover?.life_cover_name || ""}
              </p>
            ) : isLoading ? (
              <LoaderTwo height={12} />
            ) : null}

            <button
              className="btn btn-sm btn-primary"
              data-bs-toggle="modal"
              data-bs-target="#other-cover"
              type="button"
              onClick={() => setEditData(null)}
            >
              Add Other Cover
            </button>

            <div className="col-md-12 mb-3 mx- mt-2 table-responsive-sm">
              {allCovers?.length ? (
                <table className="table table-centered table-striped nowrap w-100 no-footer">
                  <thead>
                    <tr>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "140.8px" }}
                        aria-label="Customer: activate to sort column ascending"
                      >
                        Other Cover
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "5.8px" }}
                        aria-label="Customer: activate to sort column ascending"
                      >
                        Rate(%)
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "140.8px" }}
                        aria-label="Phone: activate to sort column ascending"
                      >
                        sum Assured
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "147.8px" }}
                      >
                        Premium
                      </th>
                      <th
                        className="sorting"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "257.8px" }}
                        aria-label="Email: activate to sort column ascending"
                      >
                        Detail Description
                      </th>

                      <th
                        className="sorting sorting_asc"
                        tabIndex="0"
                        rowSpan="1"
                        colSpan="1"
                        style={{ width: "146.8px" }}
                      ></th>
                    </tr>
                  </thead>

                  <tbody>
                    {allCovers?.map((item, i) => (
                      <tr key={i}>
                        <td className="text-capitalize">
                          {
                            covers?.get_other_cover?.filter(
                              (p) => p.id === item?.cover_id
                            )[0]?.cover_name
                          }
                        </td>
                        <td className="table-user">{item?.rate}</td>
                        <td className="table-user">{item?.sum_assured}</td>
                        <td className="text-dark">{item?.premium}</td>
                        <td>{item?.desc}</td>

                        <td className="text-center">
                          <div className="d-flex gap-2 align-content-center justify-content-center">
                            <span
                              className="text-primary cursor-p"
                              data-bs-toggle="modal"
                              data-bs-target="#other-cover"
                              onClick={() => setEditData(item)}
                            >
                              <BsPencilFill size={17} />
                            </span>
                            <span
                              className="text-danger cursor-p"
                              onClick={() =>
                                dispatch(deleteOtherCover(item?.cover_id))
                              }
                            >
                              <ImBin size={17} />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          </div>
          <div
            className="border p-3 rounded-3 my-3 position-relative"
            onSubmit={() => {}}
          >
            <h4 className="fw-semibold text-primary-1 bg-white position-absolute profile-title">
              Premium Information
            </h4>

            <div className="col-md-4">
              <div className="mb-3">
                <label htmlFor="policy_name" className="form-label">
                  Total Premium
                </label>
                <input
                  type="text"
                  id="total_premium"
                  name="total_premium"
                  className={`form-control form-control-sm`}
                  value={totalPremium || 0}
                  readOnly
                />
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-end gap-lg-2">
            <button
              style={{
                backgroundColor: "#D8D8D8",
              }}
              type="button"
              className="btn  text-[#2E3192] px-4"
              onClick={() => {
                navigate("/admin/customer-savings-policy-view");
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary text-white px-4">
              Save Changes
            </button>
          </div>
        </form>
      </main>
      <AddOtherSavingsCover
        modal_id="other-cover"
        editData={editData}
        setEditData={setEditData}
        selectedCovers={allCovers}
      />
    </>
  );
};

export default EditSavingsApplication;
